import React from "react";
import {
    Document, Page, Text, View,
    StyleSheet, pdf,
} from "@react-pdf/renderer";

const BORDER    = "0.5pt solid #000";
const FONT_SIZE = 7;
const HEADER_BG = "#BDD7EE";

/**
 * Nettoie la date pour n'afficher que JJ/MM/AAAA
 */
const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    } catch (e) {
        return dateStr;
    }
};

const styles = StyleSheet.create({
    page: {
        paddingTop:        18,
        paddingBottom:     18,
        paddingHorizontal: 18,
        fontSize:          FONT_SIZE,
        fontFamily:        "Helvetica",
    },
    headerRow: {
        flexDirection: "row",
        alignItems:    "center",
        marginBottom:  8,
    },
    headerTextBlock: { flex: 1, alignItems: "center" },
    headerTitle:     { fontSize: 9, fontFamily: "Helvetica-Bold", textAlign: "center", marginBottom: 2 },
    headerSubtitle:  { fontSize: 7, textAlign: "center", marginBottom: 2 },
    headerInfo:      { fontSize: 7, textAlign: "center", fontFamily: "Helvetica-Bold" },
    
    table:           { width: "100%", borderLeft: BORDER, borderTop: BORDER },
    row:             { flexDirection: "row" },
    cell: {
        borderRight:      BORDER,
        borderBottom:     BORDER,
        alignItems:       "center",
        justifyContent:   "center",
        paddingHorizontal: 4,
        paddingVertical:   2,
    },
    cellText:     { fontSize: FONT_SIZE, textAlign: "center" },
    cellTextBold: { fontSize: FONT_SIZE, fontFamily: "Helvetica-Bold", textAlign: "center" },
    cellTextLeft: { fontSize: FONT_SIZE, textAlign: "left", paddingLeft: 2 },
    rowEven:      { backgroundColor: "#EAF3FB" },
    rowOdd:       { backgroundColor: "#fff" },
    headerCell:   { backgroundColor: HEADER_BG },
    
    footer:       { marginTop: 15, flexDirection: "row", justifyContent: "flex-start" },
    footerText:   { fontSize: 8, fontFamily: "Helvetica-Bold" },
});

const Cell = ({ style, children, textStyle }) => (
    <View style={[styles.cell, style]}>
        <Text style={[styles.cellText, textStyle]}>{children ?? ""}</Text>
    </View>
);

const SeatsPDFDocument = ({ assignment, seats = [] }) => (
    <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>

            {/* En-tête institutionnel USTTB / FMOS */}
            <View style={styles.headerRow}>
                <View style={styles.headerTextBlock}>
                    <Text style={styles.headerTitle}>
                        UNIVERSITE DES SCIENCES, DES TECHNIQUES ET DES TECHNOLOGIES DE BAMAKO : USTTB
                    </Text>
                    <Text style={styles.headerTitle}>
                        FACULTE DE MEDECINE ET D'ODONTO-STOMATOLOGIE : FMOS
                    </Text>
                    <Text style={styles.headerSubtitle}>
                        Liste des places — {assignment?.exam_room?.name} ({assignment?.exam_room?.capacity} places)
                    </Text>
                    <Text style={styles.headerInfo}>
                        {assignment?.evaluation?.title} · {assignment?.date} · {assignment?.start_time} → {assignment?.end_time}
                    </Text>
                </View>
            </View>

            {/* Tableau principal */}
            <View style={styles.table}>

                {/* Ligne d'en-tête du tableau */}
                <View style={styles.row}>
                    <Cell style={[{ width: 30 },  styles.headerCell]} textStyle={{ fontFamily: "Helvetica-Bold" }}>N°</Cell>
                    <Cell style={[{ width: 85 },  styles.headerCell]} textStyle={{ fontFamily: "Helvetica-Bold" }}>Matricule</Cell>
                    <Cell style={[{ width: 85 },  styles.headerCell]} textStyle={{ fontFamily: "Helvetica-Bold" }}>N°CNOU</Cell>
                    <Cell style={[{ width: 110 }, styles.headerCell]} textStyle={{ fontFamily: "Helvetica-Bold" }}>Nom</Cell>
                    <Cell style={[{ width: 110 }, styles.headerCell]} textStyle={{ fontFamily: "Helvetica-Bold" }}>Prénom</Cell>
                    <Cell style={[{ width: 25 },  styles.headerCell]} textStyle={{ fontFamily: "Helvetica-Bold" }}>Sexe</Cell>
                    <Cell style={[{ width: 60 },  styles.headerCell]} textStyle={{ fontFamily: "Helvetica-Bold" }}>Date naiss.</Cell>
                    <Cell style={[{ flex: 1 },    styles.headerCell]} textStyle={{ fontFamily: "Helvetica-Bold" }}>Classe</Cell>
                    <Cell style={[{ width: 60 },  styles.headerCell]} textStyle={{ fontFamily: "Helvetica-Bold" }}>Émargement</Cell>
                </View>

                {/* Remplissage des lignes étudiants */}
                {seats.map((seat, i) => {
                    const bg = i % 2 === 0 ? styles.rowEven : styles.rowOdd;
                    return (
                        <View key={seat.seat_number || i} style={[styles.row, bg]}>
                            <Cell style={{ width: 30 }} textStyle={{ fontFamily: "Helvetica-Bold" }}>
                                {seat.seat_number}
                            </Cell>
                            <Cell style={{ width: 85 }}>
                                {seat.student?.student_number ?? "-"}
                            </Cell>
                            <Cell style={{ width: 85 }}>
                                {seat.student?.cnou_registration_number ?? "-"}
                            </Cell>
                            <Cell style={[{ width: 110 }, { alignItems: "flex-start" }]}
                                  textStyle={[styles.cellTextLeft, { fontFamily: "Helvetica-Bold" }]}>
                                {seat.student?.last_name ?? "-"}
                            </Cell>
                            <Cell style={[{ width: 110 }, { alignItems: "flex-start" }]}
                                  textStyle={styles.cellTextLeft}>
                                {seat.student?.first_name ?? "-"}
                            </Cell>
                            <Cell style={{ width: 25 }}>
                                {seat.student?.sex ?? "-"}
                            </Cell>
                             <Cell style={{ width: 60 }}>
                                {formatDate(seat.student?.birth_date)}
                            </Cell>
                            <Cell style={{ flex: 1 }}>
                                {seat.study_level?.name ?? "-"}
                            </Cell>
                            <Cell style={{ width: 60 }}>{""}</Cell>
                        </View>
                    );
                })}
            </View>

            {/* Pied de page pour saisie manuelle */}
            <View style={styles.footer} fixed>
                <Text style={styles.footerText}>
                    Les surveillants : 
                </Text>
            </View>

        </Page>
    </Document>
);

/**
 * Fonction pour générer et télécharger le fichier PDF
 */
export async function generateSeatsPDF({ assignment, seats }) {
    const blob = await pdf(
        <SeatsPDFDocument
            assignment={assignment}
            seats={seats}
        />
    ).toBlob();

    const room     = assignment?.exam_room?.code ?? "salle";
    const filename = `PV_Emargement_${room}.pdf`;

    const url = URL.createObjectURL(blob);
    const a   = document.createElement("a");
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default SeatsPDFDocument;